/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pencil, Send, Trash2 } from 'lucide-react'
import { useDebounce } from '@/hooks/useDebounce'
import {
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  type Customer,
  useCustomersQuery,
} from '@/hooks/useCustomers'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import type { ColumnDef } from '@tanstack/react-table'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import Loading from '@/components/Loading'
import { formatDateFR } from '@/lib/utils'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import { useRemindCustomerMutation } from '@/hooks/useDunning'
import { SendReminderDialog } from '@/components/dunning/SendReminderDialog'
import { ImportCustomersDialog } from '@/components/customers/ImportCustomersDialog'
import { useEntitlementsQuery } from '@/hooks/useEntitlements'
import { isReadonly } from '@/lib/access'
import clsx from 'clsx'

export default function CustomersPage() {
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [remindOpen, setRemindOpen] = useState(false)
  const [remindTarget, setRemindTarget] = useState<Customer | null>(null)
  const [editing, setEditing] = useState<Customer | null>(null)
  const [form, setForm] = useState({ name: '', phone: '', email: '' })
  const remindMut = useRemindCustomerMutation()
  const [importOpen, setImportOpen] = useState(false)

  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('customers', { search: '', pageIndex: 0, pageSize: 20 })

  const debounced = useDebounce(search, 400)

  const { data: ent } = useEntitlementsQuery(true)
  const ro = isReadonly(ent)

  const guard = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      return ro
    },
    [ro],
  )

  const {
    data: list,
    status,
    isFetching,
  } = useCustomersQuery({
    q: debounced || undefined,
    page: pageIndex + 1,
    pageSize,
    order: 'desc',
  })

  const total = list?.total ?? 0
  const items = list?.data ?? []
  const safePageSize = Math.max(1, pageSize)

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / safePageSize)),
    [total, safePageSize],
  )

  const onSearchChange = useCallback(
    (v: string) => {
      setSearch(v)
      setPageIndex(0)
    },
    [setSearch, setPageIndex],
  )

  const tableProps = {
    searchValue: search,
    onSearchChange,
    currentPageIndex: pageIndex,
    currentPageSize: pageSize,
    onPageChange: setPageIndex,
    onPageSizeChange: setPageSize,
    paginationServer: true as const,
    sortingServer: false as const,
  }

  const createMut = useCreateCustomerMutation()
  const updateMut = useUpdateCustomerMutation()
  const deleteMut = useDeleteCustomerMutation()

  const getPreferredRemindLabel = (c: Customer): string => {
    if (c.phone) return 'WhatsApp'
    if (c.email) return 'Email'
    return '—'
  }

  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Créé le',
        cell: ({ row }) => formatDateFR(row.original.createdAt),
      },
      {
        accessorKey: 'name',
        header: 'Nom',
        cell: ({ row }) => (
          <span className='font-medium underline-offset-2 hover:underline'>
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Téléphone',
        cell: ({ row }) => row.original.phone ?? '—',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => row.original.email ?? '—',
      },
      {
        id: 'remind',
        header: 'Relance',
        cell: ({ row }) => {
          const v = getPreferredRemindLabel(row.original)
          return v === '—' ? (
            <span className='text-xs text-muted-foreground'>—</span>
          ) : (
            <span className='text-xs rounded-full border px-2 py-0.5'>{v}</span>
          )
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <Send
              size={18}
              className={clsx(
                'text-blue-600',
                ro ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={(e) => {
                if (guard(e)) return
                setRemindTarget(row.original)
                setRemindOpen(true)
              }}
            />
            <Pencil
              size={18}
              className={clsx(
                'text-green-600 ml-4',
                ro ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={(e) => {
                if (guard(e)) return
                setEditing(row.original)
                setForm({
                  name: row.original.name,
                  phone: row.original.phone ?? '',
                  email: row.original.email ?? '',
                })
                setOpen(true)
              }}
            />
            <Trash2
              size={18}
              className={clsx(
                'text-red-600 ml-4',
                ro ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              )}
              onClick={(e) => {
                if (guard(e)) return
                setPendingDeleteId(row.original.id)
                setConfirmOpen(true)
              }}
            />
          </div>
        ),
      },
    ],
    [ro, guard, getPreferredRemindLabel],
  )

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', email: '', phone: '' })
    setOpen(true)
  }

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Nom requis')
      return
    }

    try {
      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          data: {
            name: form.name.trim(),
            phone: form.phone?.trim() || undefined,
            email: form.email?.trim() || undefined,
          },
        })
        toast.success('Client modifié')
      } else {
        await createMut.mutateAsync({
          name: form.name.trim(),
          phone: form.phone?.trim() || undefined,
          email: form.email?.trim() || undefined,
        })
        toast.success('Client créé')
      }
      setOpen(false)
    } catch (e: any) {
      toast.error('Erreur', {
        description: e?.response?.data?.message ?? 'Action échouée',
      })
    }
  }

  const confirmDelete = async () => {
    if (!pendingDeleteId) return
    try {
      await deleteMut.mutateAsync(pendingDeleteId)
      toast.success('Client supprimé')
      setConfirmOpen(false)
      setPendingDeleteId(null)
    } catch (e: any) {
      toast.error('Suppression échouée', {
        description: e?.response?.data?.message,
      })
    }
  }

  const manageGlogalRemind = async () => {
    const noConsentCount = items.filter(
      (c) => c.phone && !c.whatsappOptIn
    ).length
    if (noConsentCount > 0) {
      toast.warning(
        `${noConsentCount} client(s) seront contactés par WhatsApp sans consentement enregistré.`
      )
    }

    let ok = 0,
      skipped = 0,
      failed = 0

    for (const c of items) {
      try {
        let channel: 'WHATSAPP' | 'EMAIL' | null = null
        if (c.phone) channel = 'WHATSAPP'
        else if (c.email) channel = 'EMAIL'
        else channel = null

        if (!channel) {
          skipped++
          continue
        }

        const res = await remindMut.mutateAsync({
          customerId: c.id,
          channel,
          template: 'DEFAULT',
        })

        if (res.ok) ok++
        else if (res.reason === 'NO_DEBT') skipped++
      } catch {
        failed++
      }
    }

    toast.success('Relance terminée', {
      description: `${ok} envoyée(s), ${skipped} ignorée(s), ${failed} en échec`,
    })
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Clients</h3>
        <div className='flex items-center gap-2'>
          <Button disabled={ro} onClick={openCreate}>
            Ajouter
          </Button>

          <Button
            variant='secondary'
            disabled={items.length === 0 || remindMut.isPending || ro}
            onClick={manageGlogalRemind}
            title='Relancer tous les clients visibles dans la liste (défaut: WhatsApp)'
          >
            Relancer (tous les listés)
          </Button>

          <Button
            disabled={ro}
            variant='secondary'
            onClick={() => setImportOpen(true)}
          >
            Importer CSV
          </Button>
        </div>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{total}</b>
          </div>

          <DataTable<Customer, unknown>
            columns={columns}
            data={items}
            {...tableProps}
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            rowClickable
            onRowClick={(row) => navigate(`/customers/${row.id}`)}
          />

          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? 'Modifier client' : 'Ajouter client'}
        description='Renseignez le nom et (optionnel) le contact.'
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        footer={
          <ModalActions
            onConfirm={submit}
            confirmLabel='Valider'
            confirmLoading={createMut.isPending || updateMut.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <div className='grid gap-1.5'>
            <Label>Nom</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder='Nom du client'
              autoComplete='name'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Téléphone</Label>
            <Input
              value={form.phone}
              onChange={(e) =>
                setForm((f) => ({ ...f, phone: e.target.value }))
              }
              placeholder='Ex: +2250700000000'
              type='tel'
              autoComplete='tel'
            />
          </div>

          <div className='grid gap-1.5'>
            <Label>Email</Label>
            <Input
              type='email'
              value={form.email}
              onChange={(e) =>
                setForm((f) => ({ ...f, email: e.target.value }))
              }
              placeholder='client@example.com'
              autoComplete='email'
            />
          </div>
        </div>
      </AppDialog>

      <AppDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title='Supprimer le client'
        description='Cette action est irréversible.'
        footer={
          <ModalActions
            confirmVariant='destructive'
            confirmLabel='Supprimer'
            confirmLoading={deleteMut.isPending}
            onConfirm={confirmDelete}
          />
        }
      >
        <div className='px-4 py-2 text-sm'>
          Voulez-vous vraiment supprimer ce client ?
        </div>
      </AppDialog>

      <SendReminderDialog
        open={remindOpen}
        onOpenChange={setRemindOpen}
        defaultChannel='WHATSAPP'
        whatsappOptIn={remindTarget?.whatsappOptIn}
        loading={remindMut.isPending}
        onConfirm={async ({ channel, template }) => {
          if (!remindTarget) return

          if (channel === 'WHATSAPP' && !remindTarget?.phone) {
            toast.error('WhatsApp indisponible pour ce client', {
              description: 'Numéro de téléphone manquant.',
            })
            return
          }

          if (channel === 'EMAIL' && !remindTarget?.email) {
            toast.error('Email requis pour envoyer un email', {
              description: 'Ajoute un email au client puis réessaie.',
            })
            return
          }

          try {
            const res = await remindMut.mutateAsync({
              customerId: remindTarget.id,
              channel,
              template,
            })

            if (res.ok) {
              toast.success('Relance envoyée', {
                description: `${remindTarget.name} — dette: ${Math.round(
                  Number(res.debt || 0),
                ).toLocaleString('fr-FR')} CFA`,
              })
            } else {
              toast.info('Pas de dette ouverte pour ce client')
            }

            setRemindOpen(false)
            setRemindTarget(null)
          } catch (e: any) {
            toast.error("Échec de l'envoi", {
              description: e?.response?.data?.message ?? 'Erreur inconnue',
            })
          }
        }}
      />

      <ImportCustomersDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}
